import React, { useRef, useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import Search from "../../assets/icons/bx-search-alt-blue.svg";
import Typography from "../Typography/Typography";
import Divider from "../Divider/Divider";
import useDailyPraticeTime from "../../store/Querys/useDailyPracticeTime";
import useOrganisation from "../../store/Querys/useOrganisation";

dayjs.extend(customParseFormat);

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
);

const DailyPracticeTimeChart = ({ subUserId }: { subUserId: string }) => {
  const { data: adminData } = useOrganisation();
  const orgId = adminData?.organizations?.[0]?.id || "";
  const { data } = useDailyPraticeTime(orgId, subUserId);
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef(null);
  const gradientRef = useRef<CanvasGradient | null>(null);

  const [selectedRange, setSelectedRange] = useState<"7" | "28" | "all">("7");
  const [filteredData, setFilteredData] = useState<
    { date: string; totalPracticeTime: number }[]
  >([]);

  const calculateDateRangeText = (range: "7" | "28" | "all") => {
    const today = dayjs();
    let startDate, endDate;

    if (range === "7") {
      startDate = today.subtract(6, "days");
      endDate = today;
    } else {
      startDate = today.subtract(27, "days");
      endDate = today;
    }

    return `${startDate.format("D MMM")} - ${endDate.format("D MMM")}`;
  };

  const filterDataByRange = (range: "7" | "28" | "all") => {
    const today = dayjs();
    let filtered = [];
    let historicalSum = 0;

    if (!data?.data) return;

    if (range === "7" || range === "28") {
      const daysLimit = range === "7" ? 7 : 28;
      const lastNDaysData = data.data.filter(
        (item) => today.diff(dayjs(item.date), "day") <= daysLimit
      );
      const historicalData = data.data.filter(
        (item) => today.diff(dayjs(item.date), "day") > daysLimit
      );

      historicalSum = historicalData.reduce(
        (sum, item) => sum + item.totalPracticeTime,
        0
      );

      // Case when all data is outside the selected range
      if (lastNDaysData.length === 0) {
        filtered = [
          {
            date: today.subtract(daysLimit, "days").format("YYYY-MM-DD"),
            totalPracticeTime: 0,
          },
          {
            date: today.subtract(daysLimit, "days").format("YYYY-MM-DD"),
            totalPracticeTime: 0,
          },
          {
            date: today.format("YYYY-MM-DD"),
            totalPracticeTime: historicalSum,
          },
        ];
      } else if (lastNDaysData.length === 1) {
        filtered = [
          {
            date: today.subtract(daysLimit, "days").format("YYYY-MM-DD"),
            totalPracticeTime: 0,
          },
          {
            date: today.subtract(daysLimit, "days").format("YYYY-MM-DD"),
            totalPracticeTime: 0,
          },
          ...lastNDaysData,
        ];
      } else {
        if (historicalSum > 0) {
          filtered = [
            ...lastNDaysData,
            {
              date: today.format("YYYY-MM-DD"),
              totalPracticeTime: historicalSum,
            },
          ];
        } else {
          filtered = [...lastNDaysData];
        }
      }
    } else {
      if (data.data.length === 1) {
        filtered = [
          {
            date: today.subtract(1, "days").format("YYYY-MM-DD"),
            totalPracticeTime: 0,
          },
          {
            date: today.subtract(2, "days").format("YYYY-MM-DD"),
            totalPracticeTime: 0,
          },
          ...data.data,
        ];
      } else {
        filtered = data.data;
      }
    }

    // Accumulated Data calculation
    let cumulativeSum = 0;
    const accumulatedData = filtered.map((item) => {
      cumulativeSum += item.totalPracticeTime;
      return { ...item, totalPracticeTime: cumulativeSum };
    });

    // Apply threshold adjustments if there are multiple points
    if (accumulatedData.length > 1) {
      const values = accumulatedData.map((item) => item.totalPracticeTime);
      const minValue = Math.min(...values);
      const maxValue = Math.max(...values);

      const minimumThreshold = minValue * 0.8;
      const maximumThreshold = maxValue * 1.2;

      // Adjust the data if needed
      const adjustedData = accumulatedData.map((item) => {
        if (item.totalPracticeTime === minValue) {
          return { ...item, totalPracticeTime: minimumThreshold };
        }
        if (item.totalPracticeTime === maxValue) {
          return { ...item, totalPracticeTime: maximumThreshold };
        }
        return item;
      });

      // Set the adjusted data
      setFilteredData(adjustedData);
    } else {
      setFilteredData(accumulatedData);
    }
  };

  useEffect(() => {
    filterDataByRange(selectedRange);
  }, [selectedRange, data]);

  useEffect(() => {
    if (!gradientRef.current) {
      const ctx = document.createElement("canvas").getContext("2d");
      if (ctx) {
        const gradientBg = ctx.createLinearGradient(0, 0, 0, 155);
        gradientBg.addColorStop(0, "#2E3BC7");
        gradientBg.addColorStop(1, "white");
        gradientRef.current = gradientBg;
      }
    }
  }, []);

  const dateData = calculateDateRangeText(selectedRange);
  const chartValues = filteredData.map((item) => item.totalPracticeTime);
  const chartLabels = filteredData.map((item) =>
    dayjs(item.date).format("DD MMM")
  );

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        data: chartValues,
        fill: true,
        borderColor: "#2E3BC7",
        borderWidth: 1,
        backgroundColor: gradientRef.current || "#2E3BC7",
        tension: 0.3,
        pointRadius: 0,
        pointHoverRadius: 0,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
      datalabels: { display: false },
    },
    scales: {
      y: { display: false },
      x: {
        display: false,
        min: 0,
        max: chartValues.length > 1 ? chartValues.length - 1 : 1,
        ticks: { display: false },
        grid: { display: false },
      },
    },
  };

  return (
    <div
      ref={containerRef}
      className={
        "flex flex-col gap-16 rounded-20 border border-light-grey-10 bg-white p-24"
      }
    >
      <div>
        <Typography type={"Header3"}>Overall practice time</Typography>
        <Typography
          type={"Body2"}
          typeSM={"Body3"}
          className={"!text-[#868686] lg:mt-8"}
        >
          Aggregated amount of your practice time.
        </Typography>
      </div>

      <Divider marginY={"my-0 !bg-light-grey-10"} />

      {data && data?.totalPages ? (
        <div className={"flex justify-center gap-2"}>
          {["7", "28", "all"].map((range) => (
            <div
              key={range}
              onClick={() => setSelectedRange(range as "7" | "28" | "all")}
              className={`flex w-1/3 justify-center rounded-full border py-4 ${
                selectedRange === range
                  ? "border-primary bg-primary"
                  : "border-secondary cursor-pointer hover:bg-[#F5F5FC]"
              }`}
            >
              <Typography
                type={"Body3"}
                className={`${
                  selectedRange === range ? "text-white" : "!text-[#282828b3]"
                }`}
              >
                {range === "all" ? "All time" : `${range} days`}
              </Typography>
            </div>
          ))}
        </div>
      ) : (
        <></>
      )}

      {data && data?.totalPages > 0 ? (
        <div className={"mt-32 h-150 w-full"}>
          <Line ref={chartRef} data={chartData} options={chartOptions} />
        </div>
      ) : (
        <div className={"my-60 flex w-full flex-col items-center"}>
          <div className={"rounded-100 bg-light-grey-10 p-16"}>
            <img src={Search} alt={"no data"} />
          </div>
          <Typography type={"Body2"} className={"pb-10 pt-20"}>
            No data
          </Typography>
          <Typography type={"Body2"} className={"!text-[#868686]"}>
            No data available.
          </Typography>
        </div>
      )}

      {data && data?.totalPages > 0 && (
        <div>
          <Divider marginY={"my-0 !bg-light-grey-10"} />
          <Typography
            type={"Body3"}
            className={"mt-10 flex gap-4 !text-[#868686]"}
          >
            {selectedRange === "all"
              ? "All time"
              : `Last ${selectedRange === "7" ? `7 days` : `28 days`}: `}
            {selectedRange !== "all" && (
              <Typography type={"Body3"}>{dateData}</Typography>
            )}
          </Typography>
        </div>
      )}
    </div>
  );
};

export default DailyPracticeTimeChart;
